<script lang="ts">
    import { fly } from "svelte/transition";
    import {
        audioManagerFileStore,
        audioManagerVisibilityStore,
        bubbleSoundStore,
    } from "../../Stores/AudioManagerStore";
    import { HtmlUtils } from "../../WebRtc/HtmlUtils";
    import { LL, locale } from "../../../i18n/i18n-svelte";
    import type { Locales } from "../../../i18n/i18n-types";
    import { displayableLocales, setCurrentLocale } from "../../Utils/locales";
    import { gameManager } from "../../Phaser/Game/GameManager";

    import { analyticsClient } from "../../Administration/AnalyticsClient";
    import { localUserStore } from "../../Connection/LocalUserStore";
    import { videoQualityStore } from "../../Stores/MediaStore";
    import { screenShareQualityStore } from "../../Stores/ScreenSharingStore";
    import { volumeProximityDiscussionStore } from "../../Stores/PeerStore";
    import { bandwidthConstrainedPreferenceStore } from "../../Stores/BandwidthConstrainedPreferenceStore";
    import InputSwitch from "../Input/InputSwitch.svelte";
    import RangeSlider from "../Input/RangeSlider.svelte";
    import SoundSelect from "../Input/SoundSelect.svelte";
    import { displayVideoQualityStore } from "../../Stores/DisplayVideoQualityStore";
    import InputRadioBox from "../Input/InputRadioBox.svelte";
    import {
        IconAntennaBarsLow,
        IconAntennaBarsMid,
        IconAntennaBarsHigh,
        IconAdjustements,
        IconLanguage,
        IconDoorExit,
        IconScreenShare,
        IconCameraUp,
    } from "@wa-icons";

    let fullscreen: boolean = localUserStore.getFullscreen();
    let notification: boolean = localUserStore.getNotification();
    let allowPictureInPicture: boolean = localUserStore.getAllowPictureInPicture();
    let blockAudio: boolean = localUserStore.getBlockAudio();
    let forceCowebsiteTrigger: boolean = localUserStore.getForceCowebsiteTrigger();
    let ignoreFollowRequests: boolean = localUserStore.getIgnoreFollowRequests();
    let decreaseAudioPlayerVolumeWhileTalking: boolean = localUserStore.getDecreaseAudioPlayerVolumeWhileTalking();
    let disableAnimations: boolean = localUserStore.getDisableAnimations();
    let valueLocale: string = $locale;
    let valueCameraPrivacySettings = localUserStore.getCameraPrivacySettings();
    let valueMicrophonePrivacySettings = localUserStore.getMicrophonePrivacySettings();
    const initialVideoQuality = localUserStore.getVideoQuality();
    let valueVideoQuality = initialVideoQuality === "high" ? 3 : initialVideoQuality === "low" ? 1 : 2;
    const initialScreenShareQuality = localUserStore.getScreenShareQuality();
    let valueScreenShareQuality =
        initialScreenShareQuality === "high" ? 3 : initialScreenShareQuality === "low" ? 1 : 2;
    let bandwidthConstrainedPreference = localUserStore.getBandwidthConstrainedScreenSharePreference();

    let volumeProximityDiscussion = localUserStore.getVolumeProximityDiscussion();

    let previewCameraPrivacySettings = valueCameraPrivacySettings;
    let previewMicrophonePrivacySettings = valueMicrophonePrivacySettings;

    let valueBubbleSound = localUserStore.getBubbleSound();
    let videoQualityStats = localUserStore.getDisplayVideoQualityStats();

    async function updateLocale() {
        await setCurrentLocale(valueLocale as Locales);
    }

    function updateVideoQuality() {
        let value: "low" | "recommended" | "high";

        switch (valueVideoQuality) {
            case 1:
                value = "low";
                break;
            case 3:
                value = "high";
                break;
            default:
                value = "recommended";
                break;
        }

        videoQualityStore.setQuality(value);
    }

    function updateScreenShareQuality() {
        let value: "low" | "recommended" | "high";

        switch (valueScreenShareQuality) {
            case 1:
                value = "low";
                break;
            case 3:
                value = "high";
                break;
            default:
                value = "recommended";
                break;
        }

        screenShareQualityStore.setQuality(value);
    }

    function updateBandwidthConstrainedPreference() {
        bandwidthConstrainedPreferenceStore.setPreference(bandwidthConstrainedPreference);
    }

    function changeFullscreen() {
        // Analytics Client
        analyticsClient.settingFullscreen(fullscreen ? "true" : "false");

        const body = HtmlUtils.querySelectorOrFail("body");
        if (body) {
            if (document.fullscreenElement !== null && !fullscreen) {
                document.exitFullscreen().catch((e) => console.error(e));
            } else {
                document.documentElement.requestFullscreen().catch((e) => console.error(e));
            }
            localUserStore.setFullscreen(fullscreen);
        }
    }

    function changeNotification() {
        // Analytics Client
        analyticsClient.settingNotification(notification ? "true" : "false");

        if (Notification.permission === "granted") {
            localUserStore.setNotification(notification);
        } else {
            Notification.requestPermission()
                .then((response) => {
                    if (response === "granted") {
                        localUserStore.setNotification(notification);
                    } else {
                        localUserStore.setNotification(false);
                        notification = false;
                    }
                })
                .catch((e) => console.error(e));
        }
    }

    function changePictureInPicture() {
        // Analytics Client
        analyticsClient.settingPictureInPicture(allowPictureInPicture ? "true" : "false");

        localUserStore.setAllowPictureInPicture(allowPictureInPicture);
    }

    function changeBlockAudio() {
        if (blockAudio) {
            audioManagerFileStore.unloadAudio();
            audioManagerVisibilityStore.set("disabledBySettings");
        }
        localUserStore.setBlockAudio(blockAudio);
    }

    function changeForceCowebsiteTrigger() {
        // Analytics Client
        analyticsClient.settingAskWebsite(forceCowebsiteTrigger ? "true" : "false");

        localUserStore.setForceCowebsiteTrigger(forceCowebsiteTrigger);
    }

    function changeIgnoreFollowRequests() {
        // Analytics Client
        analyticsClient.settingRequestFollow(ignoreFollowRequests ? "true" : "false");

        localUserStore.setIgnoreFollowRequests(ignoreFollowRequests);
    }

    function changeDecreaseAudioPlayerVolumeWhileTalking() {
        // Analytics Client
        analyticsClient.settingDecreaseAudioVolume(decreaseAudioPlayerVolumeWhileTalking ? "true" : "false");

        localUserStore.setDecreaseAudioPlayerVolumeWhileTalking(decreaseAudioPlayerVolumeWhileTalking);
    }

    function changeDisableAnimations() {
        localUserStore.setDisableAnimations(disableAnimations);
        if (disableAnimations) {
            gameManager.getCurrentGameScene().animatedTiles.pause();
        } else {
            gameManager.getCurrentGameScene().animatedTiles.resume();
        }
    }

    function changeCameraPrivacySettings() {
        // Analytics Client
        analyticsClient.settingMicrophone(valueCameraPrivacySettings ? "true" : "false");

        if (valueCameraPrivacySettings !== previewCameraPrivacySettings) {
            previewCameraPrivacySettings = valueCameraPrivacySettings;
            localUserStore.setCameraPrivacySettings(valueCameraPrivacySettings);
        }
    }

    function changeMicrophonePrivacySettings() {
        // Analytics Client
        analyticsClient.settingCamera(valueMicrophonePrivacySettings ? "true" : "false");

        if (valueMicrophonePrivacySettings !== previewMicrophonePrivacySettings) {
            previewMicrophonePrivacySettings = valueMicrophonePrivacySettings;
            localUserStore.setMicrophonePrivacySettings(valueMicrophonePrivacySettings);
        }
    }

    function updateVolumeProximityDiscussion() {
        analyticsClient.settingAudioVolume();
        localUserStore.setVolumeProximityDiscussion(volumeProximityDiscussion);
        volumeProximityDiscussionStore.set(volumeProximityDiscussion);
    }

    function changeBubbleSound() {
        localUserStore.setBubbleSound(valueBubbleSound);
        bubbleSoundStore.set(valueBubbleSound);
    }

    function changeVideoQualityStats() {
        localUserStore.setDisplayVideoQualityStats(videoQualityStats);
        displayVideoQualityStore.set(videoQualityStats);
    }

    function getBubbleSoundUrl(bubbleSound: string): string {
        return `/resources/objects/webrtc-in-${bubbleSound}.mp3`;
    }
</script>

<div class="divide-y divide-white/20" transition:fly={{ x: -700, duration: 250 }}>
    <section class=" p-0 first:pt-0 pt-8 m-0">
        <div class="bg-contrast font-bold text-lg p-4 flex items-center">
            <div class="me-4 opacity-50"><IconCameraUp /></div>
            {$LL.menu.settings.videoBandwidth.title()}
        </div>
        <div class="flex w-full mb-6 mt-2 ps-6 justify-center">
            <div class="flex flex-col w-10/12 lg:w-6/12">
                <ul class="flex justify-between w-full px-[10px] mb-8">
                    <li
                        class="flex justify-center relative {valueVideoQuality === 1
                            ? 'opacity-100 font-bold'
                            : 'opacity-50 hover:opacity-80'}"
                    >
                        <IconAntennaBarsLow />
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <span
                            class="absolute -bottom-4 cursor-pointer"
                            on:click|preventDefault={() => (valueVideoQuality = 1)}
                            >{$LL.menu.settings.videoBandwidth.low()}</span
                        >
                    </li>
                    <li
                        class="flex justify-center relative {valueVideoQuality === 2
                            ? 'opacity-100 font-bold'
                            : 'opacity-50 hover:opacity-80'}"
                    >
                        <IconAntennaBarsMid />
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <span
                            class="absolute -bottom-4 cursor-pointer"
                            on:click|preventDefault={() => (valueVideoQuality = 2)}
                            >{$LL.menu.settings.videoBandwidth.recommended()}</span
                        >
                    </li>
                    <li
                        class="flex justify-center relative {valueVideoQuality === 3
                            ? 'opacity-100 font-bold'
                            : 'opacity-50 hover:opacity-80'}"
                    >
                        <IconAntennaBarsHigh />
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <span
                            class="absolute -bottom-4 cursor-pointer"
                            on:click|preventDefault={() => (valueVideoQuality = 3)}
                            >{$LL.menu.settings.videoBandwidth.high()}</span
                        >
                    </li>
                </ul>
                <RangeSlider
                    buttonShape="square"
                    min={1}
                    max={3}
                    step={1}
                    bind:value={valueVideoQuality}
                    onChange={updateVideoQuality}
                />
            </div>
        </div>
    </section>
    <section class="flex flex-col p-0 first:pt-0 pt-8 m-0">
        <div class="bg-contrast font-bold text-lg p-4 flex items-center">
            <div class="me-4 opacity-50"><IconScreenShare /></div>

            {$LL.menu.settings.shareScreenBandwidth.title()}
        </div>
        <div class="flex w-full mb-6 mt-2 ps-6 justify-center">
            <div class="flex flex-col w-10/12 lg:w-6/12">
                <ul class="flex justify-between w-full px-[10px] mb-8">
                    <li
                        class="flex relative {valueScreenShareQuality === 1
                            ? 'opacity-100 font-bold'
                            : 'opacity-50 hover:opacity-80'}"
                    >
                        <IconAntennaBarsLow />
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <span
                            class="absolute -bottom-4 cursor-pointer"
                            on:click|preventDefault={() => (valueScreenShareQuality = 1)}
                            >{$LL.menu.settings.shareScreenBandwidth.low()}</span
                        >
                    </li>
                    <li
                        class="flex justify-center relative {valueScreenShareQuality === 2
                            ? 'opacity-100 font-bold'
                            : 'opacity-50 hover:opacity-80'}"
                    >
                        <IconAntennaBarsMid />
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <span
                            class="absolute -bottom-4 cursor-pointer"
                            on:click|preventDefault={() => (valueScreenShareQuality = 2)}
                            >{$LL.menu.settings.shareScreenBandwidth.recommended()}</span
                        >
                    </li>
                    <li
                        class="flex justify-center relative {valueScreenShareQuality === 3
                            ? 'opacity-100 font-bold'
                            : 'opacity-50 hover:opacity-80'}"
                    >
                        <IconAntennaBarsHigh />
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <span
                            class="absolute -bottom-4 cursor-pointer"
                            on:click|preventDefault={() => (valueScreenShareQuality = 3)}
                            >{$LL.menu.settings.shareScreenBandwidth.high()}</span
                        >
                    </li>
                </ul>
                <RangeSlider
                    min={1}
                    max={3}
                    step={1}
                    bind:value={valueScreenShareQuality}
                    onChange={updateScreenShareQuality}
                    buttonShape="square"
                />
            </div>
        </div>
        <div class="input-label">
            <div class="grow font-light text-center font-semibold">
                {$LL.menu.settings.bandwidthConstrainedPreference.title()}
            </div>
        </div>
        <div class="mt-2 p-2 flex gap-4 justify-center items-stretch">
            <InputRadioBox
                value="maintain-resolution"
                label={$LL.menu.settings.bandwidthConstrainedPreference.maintainResolutionTitle()}
                bind:group={bandwidthConstrainedPreference}
                onChange={updateBandwidthConstrainedPreference}
                outerClass="flex-1"
            >
                <em>{$LL.menu.settings.bandwidthConstrainedPreference.maintainResolutionDescription()}</em>
            </InputRadioBox>
            <InputRadioBox
                value="maintain-framerate"
                label={$LL.menu.settings.bandwidthConstrainedPreference.maintainFramerateTitle()}
                bind:group={bandwidthConstrainedPreference}
                onChange={updateBandwidthConstrainedPreference}
                outerClass="flex-1"
            >
                <em>{$LL.menu.settings.bandwidthConstrainedPreference.maintainFramerateDescription()}</em>
            </InputRadioBox>
            <InputRadioBox
                value="balanced"
                label={$LL.menu.settings.bandwidthConstrainedPreference.balancedTitle()}
                bind:group={bandwidthConstrainedPreference}
                onChange={updateBandwidthConstrainedPreference}
                outerClass="flex-1"
            >
                <em>{$LL.menu.settings.bandwidthConstrainedPreference.balancedDescription()}</em>
            </InputRadioBox>
        </div>

        <div class="bg-contrast font-bold text-lg p-4 flex items-center">
            <div class="me-4 opacity-50"><IconAdjustements /></div>

            {$LL.menu.settings.proximityDiscussionVolume()}
        </div>

        <div class="flex w-full justify-center">
            <div class="flex flex-col w-10/12 lg:w-6/12">
                <ul class="flex justify-between w-full px-[10px] mb-5">
                    <li class="flex justify-center relative">
                        <span class="absolute">0</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">1</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">2</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">3</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">4</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">5</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">6</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">7</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">8</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">9</span>
                    </li>
                    <li class="flex justify-center relative">
                        <span class="absolute">10</span>
                    </li>
                </ul>
                <RangeSlider
                    min={0}
                    max={1}
                    step={0.1}
                    bind:value={volumeProximityDiscussion}
                    onChange={updateVolumeProximityDiscussion}
                />
            </div>
        </div>
    </section>
    <section class="flex flex-col p-0 first:pt-0 pt-8 m-0">
        <div class="bg-contrast font-bold text-lg p-4 flex items-center">
            <div class="me-4 opacity-50"><IconLanguage /></div>
            {$LL.menu.settings.language.title()}
        </div>
        <div class="mt-2 p-2">
            <select
                class="w-full languages-switcher bg-contrast rounded border border-solid border-white/20 mb-0"
                bind:value={valueLocale}
                on:change={updateLocale}
            >
                {#each displayableLocales as locale (locale.id)}
                    <option value={locale.id}>
                        {`${
                            locale.language ? locale.language.charAt(0).toUpperCase() + locale.language.slice(1) : ""
                        } (${locale.region})`}
                    </option>
                {/each}
            </select>
        </div>
    </section>
    <section class="flex flex-col p-0 first:pt-0 pt-8 m-0">
        <div class="tooltip">
            <div class="group bg-contrast font-bold text-lg p-4 flex items-center relative">
                <div class="me-4 opacity-50"><IconDoorExit /></div>
                <div class="grow">
                    <div>{$LL.menu.settings.privacySettings.title()}</div>
                    <div class="text-sm italic text-white/50">{$LL.menu.settings.privacySettings.explanation()}</div>
                </div>
            </div>
        </div>

        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="cam-toggle"
                bind:value={valueCameraPrivacySettings}
                onChange={changeCameraPrivacySettings}
                label={$LL.menu.settings.privacySettings.cameraToggle()}
            />
        </div>

        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="mic-toggle"
                bind:value={valueMicrophonePrivacySettings}
                onChange={changeMicrophonePrivacySettings}
                label={$LL.menu.settings.privacySettings.microphoneToggle()}
            />
        </div>
    </section>
    <section class="flex flex-col p-0 first:pt-0 pt-8 m-0">
        <div class="bg-contrast font-bold text-lg p-4 flex items-center">
            <div class="me-4 opacity-50"><IconAdjustements /></div>
            {$LL.menu.settings.otherSettings()}
        </div>

        <div class="mt-2 p-2">
            <SoundSelect
                id="bubble-sound"
                bind:value={valueBubbleSound}
                onChange={changeBubbleSound}
                label={$LL.menu.settings.bubbleSound()}
                options={[
                    { value: "ding", label: $LL.menu.settings.bubbleSoundOptions.ding() },
                    { value: "wobble", label: $LL.menu.settings.bubbleSoundOptions.wobble() },
                ]}
                getSoundUrl={getBubbleSoundUrl}
                playLabel="▶"
            />
        </div>

        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="fullscreen-toggle"
                bind:value={fullscreen}
                onChange={changeFullscreen}
                label={$LL.menu.settings.fullscreen()}
            />
        </div>
        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="notification-toggle"
                bind:value={notification}
                onChange={changeNotification}
                label={$LL.menu.settings.notifications()}
            />
        </div>
        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="picture-in-picture-toggle"
                bind:value={allowPictureInPicture}
                onChange={changePictureInPicture}
                label={$LL.menu.settings.enablePictureInPicture()}
            />
        </div>
        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="cowebsiteTrigger-toggle"
                bind:value={forceCowebsiteTrigger}
                onChange={changeForceCowebsiteTrigger}
                label={$LL.menu.settings.cowebsiteTrigger()}
            />
        </div>

        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="cowebsiteTrigger-toggle"
                bind:value={ignoreFollowRequests}
                onChange={changeIgnoreFollowRequests}
                label={$LL.menu.settings.ignoreFollowRequest()}
            />
        </div>
        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="decreaseAudioPlayerVolumeWhileTalking-toggle"
                bind:value={decreaseAudioPlayerVolumeWhileTalking}
                onChange={changeDecreaseAudioPlayerVolumeWhileTalking}
                label={$LL.audio.manager.reduce()}
            />
        </div>

        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="changeBlockAudio"
                bind:value={blockAudio}
                onChange={changeBlockAudio}
                label={$LL.menu.settings.blockAudio()}
            />
        </div>

        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="changeDisableAnimations"
                bind:value={disableAnimations}
                onChange={changeDisableAnimations}
                label={$LL.menu.settings.disableAnimations()}
            />
        </div>

        <div class="flex cursor-pointer items-center relative m-4">
            <InputSwitch
                id="changeVideoQualityStats"
                bind:value={videoQualityStats}
                onChange={changeVideoQualityStats}
                label={$LL.menu.settings.displayVideoQualityStats()}
            />
        </div>
    </section>
</div>

<style lang="scss">
</style>
